import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { articles } from "@/mocks/news";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar from "@/components/feature/FilterBar";
import SortTabs from "@/components/feature/SortTabs";
import NewsCard from "@/components/feature/NewsCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  applyFilter,
  applySort,
  defaultFilter,
  paginate,
  searchArticles,
  type NewsFilterState,
  type SortKey,
} from "@/utils/news";

const PAGE_SIZE = 6;
const hotKeywords = ["城市", "教育", "健康", "文旅", "新能源", "民生", "科技", "环保"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();

  const [input, setInput] = useState(keyword);
  const [filter, setFilter] = useState<NewsFilterState>(defaultFilter);
  const [sort, setSort] = useState<SortKey>("time");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInput(keyword);
    setPage(1);
  }, [keyword]);

  const matched = useMemo(() => searchArticles(articles, keyword), [keyword]);

  const result = useMemo(() => {
    const filtered = applyFilter(matched, filter);
    return applySort(filtered, sort);
  }, [matched, filter, sort]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = paginate(result, currentPage, PAGE_SIZE);

  usePageMeta({
    title: keyword ? `“${keyword}”的搜索结果 - 51爆料网` : "搜索 - 51爆料网",
    description: keyword
      ? `51爆料网关于“${keyword}”的搜索结果，共 ${result.length} 条相关爆料与热点资讯，支持筛选与排序。`
      : "51爆料网全站搜索，输入关键词快速查找社会爆料与热点新闻。",
    keywords: `${keyword},51爆料网,搜索,爆料,热点资讯`,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
    setPage(1);
  };

  const updateFilter = (next: NewsFilterState) => {
    setFilter(next);
    setPage(1);
  };
  const updateSort = (next: SortKey) => {
    setSort(next);
    setPage(1);
  };
  const reset = () => {
    setFilter(defaultFilter);
    setSort("time");
    setPage(1);
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-5 md:px-6 md:py-6">
          <Breadcrumb items={[{ label: "搜索结果" }]} />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            {keyword ? `搜索“${keyword}”` : "全站搜索"}
          </h1>
          <form onSubmit={submit} className="mt-5 flex max-w-2xl flex-col gap-3 sm:flex-row">
            <div className="flex h-12 flex-1 items-center gap-2 rounded-full border border-background-300 bg-background-50 px-5 focus-within:border-primary-400">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入关键词搜索爆料、热点…"
                className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="submit"
              className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-search-line" />
              搜索
            </button>
          </form>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-400">热门搜索：</span>
            {hotKeywords.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setSearchParams({ q: k })}
                className="whitespace-nowrap rounded-full bg-background-50 px-3 py-1 text-xs text-foreground-600 transition-colors hover:text-primary-600"
              >
                {k}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        {keyword ? (
          <>
            <p className="text-sm text-foreground-500">
              关键词 <span className="font-semibold text-primary-600">“{keyword}”</span> 共找到{" "}
              <span className="font-semibold text-accent-600">{result.length}</span> 条结果
            </p>

            <div className="mt-5">
              <FilterBar value={filter} onChange={updateFilter} />
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-500">可按时间、类型、地区进一步筛选</p>
              <SortTabs value={sort} onChange={updateSort} />
            </div>

            {pageItems.length > 0 ? (
              <>
                <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {pageItems.map((item) => (
                    <NewsCard key={item.id} article={item} />
                  ))}
                </div>
                <Pagination page={currentPage} totalPages={totalPages} onPageChange={setPage} />
              </>
            ) : (
              <>
                <div className="mt-5">
                  <EmptyState
                    title="没有找到相关爆料"
                    message="换个关键词试试，或降低筛选条件。以下是可能感兴趣的热门内容。"
                    onReset={reset}
                  />
                </div>
                <div className="mt-8">
                  <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                    <span className="h-5 w-1.5 rounded-full bg-accent-500" />
                    友情推荐
                  </h2>
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    {applySort(articles, "hot")
                      .slice(0, 4)
                      .map((item) => (
                        <NewsCard key={item.id} article={item} />
                      ))}
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <EmptyState
            title="输入关键词开始搜索"
            message="例如“城市防汛”“社区食堂”“新能源汽车”，即可快速找到相关爆料与报道。"
          />
        )}

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/category/all"
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-apps-2-line" />
            浏览全部爆料
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}